import React, {useEffect, useRef, useState} from 'react';
import {graph} from "../services/GraphService";
import {useGraphRelationContext} from "../services/ButtonRelationService";
import config from "../../../config";

const GraphViewWrapper = ({children, centralize, onContextMenu}) => {
    const svgRef = useRef(null);
    const gRef = useRef(null);

    const [transform, setTransform] = useState({
        x: 0,
        y: 0,
        scale: 3,
    });

    const [dragging, setDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [animating, setAnimating] = useState(false);

    const {
        graphMoveDirection, graphMoveSpeedMultiplayer, activeButtonId
    } = useGraphRelationContext()

    useEffect(() => {
        graph.svg = svgRef.current;
    }, []);

    useEffect(() => {
        if (!centralize || !centralize.value) return;
        const svg = svgRef.current;
        const g = gRef.current;
        if (!svg || !g) return;

        const svgRect = svg.getBoundingClientRect();
        const gBBox = g.getBBox();
        if (gBBox.width === 0 || gBBox.height === 0) return;

        const fitScale = Math.min(
            svgRect.width / gBBox.width,
            svgRect.height / gBBox.height,
            1
        ) * 0.9;

        if (centralize.value === 'scale') { // only scale from center
            const initialScale = 3;
            const initialDx = (svgRect.width - gBBox.width * initialScale) / 2 - gBBox.x * initialScale;
            const initialDy = (svgRect.height - gBBox.height * initialScale) / 2 - gBBox.y * initialScale;

            setTransform({x: initialDx, y: initialDy, scale: initialScale});

            requestAnimationFrame(() => {
                setAnimating(true);
                const dx = (svgRect.width - gBBox.width * fitScale) / 2 - gBBox.x * fitScale;
                const dy = (svgRect.height - gBBox.height * fitScale) / 2 - gBBox.y * fitScale;

                requestAnimationFrame(() => {
                    setTransform({x: dx, y: dy, scale: fitScale});
                    setTimeout(() => setAnimating(false), 700);
                });
            });
        } else { // centralize and scale
            const scaleX = svgRect.width / gBBox.width;
            const scaleY = svgRect.height / gBBox.height;
            const fitScale = Math.min(scaleX, scaleY, 1) * 0.9;
            const dx = (svgRect.width - gBBox.width * fitScale) / 2 - gBBox.x * fitScale;
            const dy = (svgRect.height - gBBox.height * fitScale) / 2 - gBBox.y * fitScale;
            setAnimating(true);
            requestAnimationFrame(() => {
                setTransform({x: dx, y: dy, scale: fitScale});
                setTimeout(() => setAnimating(false), 700);
            });
        }

    }, [centralize]);

    const rafRef = useRef(null);
    const moveDirectionRef = useRef(null);
    const moveSpeedRef = useRef(0);

    useEffect(() => {
        if(!activeButtonId){
            moveDirectionRef.current = null
            moveSpeedRef.current = 0
            return;
        }
        moveDirectionRef.current = graphMoveDirection;
        moveSpeedRef.current = graphMoveSpeedMultiplayer;
    }, [graphMoveDirection, graphMoveSpeedMultiplayer,activeButtonId]);

    useEffect(() => {
        const pan = () => {
            const direction = moveDirectionRef.current;
            const speedMultiplier = moveSpeedRef.current;

            if (direction && speedMultiplier > 0) {
                setTransform(prev => {
                    const speed = (config.graphBaseMoveSpeed * speedMultiplier) * prev.scale;

                    let dx = 0;
                    let dy = 0;

                    switch (direction) {
                        case 'left':
                            dx = speed;
                            break;
                        case 'right':
                            dx = -speed;
                            break;
                        case 'top':
                            dy = speed;
                            break;
                        case 'bottom':
                            dy = -speed;
                            break;
                    }

                    return {...prev, x: prev.x + dx, y: prev.y + dy};
                });
            }

            rafRef.current = requestAnimationFrame(pan);
        };

        rafRef.current = requestAnimationFrame(pan);

        return () => cancelAnimationFrame(rafRef.current);
    }, []);


    const handleWheel = e => {
        const svg = svgRef.current;
        const rect = svg.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        if (e.shiftKey) {
            e.persist();
            setTransform(prev => ({
                ...prev,
                y: prev.y - e.deltaY / 3,
                x: prev.x - e.deltaX / 3
            }));
        } else {
            const scaleFactor = e.deltaY > 0 ? 0.9 : 1.1;
            setTransform(prev => {
                const newScale = Math.min(3, Math.max(0.1, prev.scale * scaleFactor));
                const dx = mouseX - (mouseX - prev.x) * (newScale / prev.scale);
                const dy = mouseY - (mouseY - prev.y) * (newScale / prev.scale);
                return {x: dx, y: dy, scale: newScale};
            });
        }
    };

    const handleMouseDown = e => {
        setDragging(true);
        setDragStart({x: e.clientX, y: e.clientY});
    };

    const handleMouseMove = e => {
        if (!dragging) return;
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        setTransform(prev => ({...prev, x: prev.x + dx, y: prev.y + dy}));
        setDragStart({x: e.clientX, y: e.clientY});
    };

    const handleMouseUp = () => setDragging(false);

    return (
        <svg
            ref={svgRef}
            width="100%"
            height="100%"
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onContextMenu={onContextMenu}
            style={{cursor: dragging ? 'grabbing' : 'grab'}}
            /*
             * While the whole tree is being scaled into place there is nothing to
             * look at closely, and every shadow on screen would be re-blurred at
             * every frame of it. They come back the moment it settles.
             */
            className={'graph-view-svg' + (animating ? ' animating' : '')}
        >
            {/*
              * Defined once for the whole canvas.
              *
              * These used to be declared inside every node, so a tree of fifty
              * nodes carried fifty copies of the same two filters under the same
              * two ids - and an id in a document means one thing, so forty-nine
              * of them were dead weight the browser still had to parse. The
              * arrowhead is new: edges have always asked for it by name and
              * there was never anything there to find.
              */}
            <defs>
                {/*
                  * One blur apiece, and a modest one.
                  *
                  * A filter is re-rasterised at every frame of a transform, so
                  * the cost of these is paid fifty times over on a tree of fifty
                  * nodes every time the canvas is scaled into view. A pair of
                  * them with a radius of ten was enough to make that animation
                  * stutter. The cards carry a real border, so the shadow only
                  * has to lift them off the ground - it is not what separates
                  * them from it.
                  */}
                <filter id="nodeShadow" x="-25%" y="-25%" width="150%" height="150%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3.5" floodColor="rgba(16, 24, 40, 0.10)"/>
                </filter>

                {/* hover only, so one node at a time - it can afford a little more */}
                <filter id="nodeShadowRaised" x="-30%" y="-30%" width="160%" height="160%">
                    <feDropShadow dx="0" dy="6" stdDeviation="7" floodColor="rgba(16, 24, 40, 0.16)"/>
                </filter>

                <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor="rgba(16, 24, 40, 0.08)"/>
                </filter>

                {/*
                  * Where an edge lands. Small and solid - an arrow big enough to
                  * notice on its own is an arrow that crowds the node it points
                  * at, and there are as many of these as there are buttons.
                  */}
                <marker
                    id="arrowhead"
                    markerWidth="7"
                    markerHeight="7"
                    refX="5.4"
                    refY="2.6"
                    orient="auto"
                    markerUnits="strokeWidth"
                >
                    <path d="M 0.6 0.4 L 5.6 2.6 L 0.6 4.8 Z" fill="context-stroke"/>
                </marker>
            </defs>

            <g
                id="graph-translatable-g"
                ref={gRef}
                transform={`translate(${transform.x}, ${transform.y}) scale(${transform.scale})`}
                style={{
                    transition: animating
                        ? 'transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)'
                        : 'none',
                }}
            >
                {children({
                    gTransform: transform,
                    gRef,
                    svgRef
                })}
            </g>
        </svg>
    );
};

export default GraphViewWrapper;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/containers/GraphView/components/GraphViewWrapper.jsx